import type { Contract } from '@gmn-portal/backend';
import { ChevronDown, ChevronRight, MoreHorizontal } from 'lucide-react';
import { DateTime } from 'luxon';
import { forwardRef, useImperativeHandle, useMemo, useRef, useState } from 'react';
import type { Id } from 'react-calendar-timeline';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useContracts, useCustomer } from '@/core/api';
import { useIntl } from '@/core/i18n/use-intl';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui';
import { TimelineView, type TimelineViewHandle } from '@/shared/components/ui/timeline';
import { cn } from '@/shared/utils';

interface ContractsTimelineProps {
  contracts: Contract[];
  groupByCustomer?: boolean;
  onDelete?: (contract: Contract) => void;
  onCopy?: (contract: Contract) => void;
}

export interface ContractsTimelineHandle {
  handleExpandAll: () => void;
  handleCollapseAll: () => void;
  handleZoomIn: () => void;
  handleZoomOut: () => void;
}

interface CustomerTimelineItemProps {
  customerId: string;
  startTime: number;
  endTime: number;
}

const CustomerTimelineItemTitle = ({ customerId }: { customerId: string }) => {
  const { data: customer } = useCustomer(customerId);
  return <span className="text-xs text-foreground">{customer?.name || '-'}</span>;
};

function CustomerTimelineItem({ customerId, startTime, endTime }: CustomerTimelineItemProps) {
  return {
    id: `customer-span-${customerId}`,
    group: `customer-${customerId}`,
    title: <CustomerTimelineItemTitle customerId={customerId} />,
    start_time: startTime,
    end_time: endTime,
    className: cn('bg-primary/10! border-primary/30! border-dashed!'),
  };
}

function CustomerGroup({
  id,
  isExpanded,
  onToggle,
}: {
  id: string;
  isExpanded?: boolean;
  onToggle?: () => void;
}) {
  const { data: customer } = useCustomer(id);
  const { data: customerContracts } = useContracts({
    where: {
      customer: {
        equals: id,
      },
    },
  });

  const hasRootContracts = (customerContracts?.docs.length ?? 0) > 0;

  return (
    <button
      type="button"
      onClick={hasRootContracts && onToggle ? onToggle : undefined}
      className="flex items-center gap-1 w-full text-left text-sm hover:bg-accent/50 rounded px-1"
      disabled={!hasRootContracts}
    >
      {hasRootContracts ? (
        <span className="text-muted-foreground">
          {isExpanded ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
        </span>
      ) : (
        <span className="w-5" /> // Spacer for alignment
      )}
      <span className="truncate">{customer?.name || '-'}</span>
    </button>
  );
}

export const ContractsTimeline = forwardRef<ContractsTimelineHandle, ContractsTimelineProps>(
  ({ contracts, groupByCustomer = false, onDelete, onCopy }, ref) => {
    const navigate = useNavigate();
    const { t } = useIntl();
    const [searchParams] = useSearchParams();
    const basePath = '/gmn/contract-management/contracts';
    const timelineRef = useRef<TimelineViewHandle>(null);

    // Initialize expandedCustomers with all customers expanded
    const initialExpandedCustomers = useMemo(() => {
      if (!groupByCustomer) return {};
      return contracts.reduce(
        (acc, contract) => {
          const customerId =
            typeof contract.customer === 'string' ? contract.customer : contract.customer?.id;
          if (customerId) {
            acc[customerId] = true;
          }
          return acc;
        },
        {} as Record<string, boolean>
      );
    }, [contracts, groupByCustomer]);

    const [expandedCustomers, setExpandedCustomers] =
      useState<Record<string, boolean>>(initialExpandedCustomers);

    // Pre-calculate customer timeline spans
    const customerSpans = useMemo(() => {
      if (!groupByCustomer) return [];

      // Group contracts by customer and calculate spans
      const spans = contracts.reduce(
        (acc, contract) => {
          const customerField = contract.customer;
          const customerId = typeof customerField === 'string' ? customerField : customerField?.id;
          if (!customerId) return acc;

          if (!acc[customerId]) {
            acc[customerId] = {
              id: customerId,
              startDate: DateTime.fromISO(contract.startDate!),
              endDate: DateTime.fromISO(contract.endDate!),
            };
          } else {
            const contractStartDate = DateTime.fromISO(contract.startDate!);
            if (contractStartDate < acc[customerId].startDate) {
              acc[customerId].startDate = contractStartDate;
            }

            const contractEndDate = DateTime.fromISO(contract.endDate!);
            if (contractEndDate > acc[customerId].endDate) {
              acc[customerId].endDate = contractEndDate;
            }
          }

          return acc;
        },
        {} as Record<
          string,
          {
            id: string;
            startDate: DateTime;
            endDate: DateTime;
          }
        >
      );

      return Object.entries(spans).map(([customerId, span]) => ({
        customerId,
        startTime: span.startDate.toMillis(),
        endTime: span.endDate.toMillis(),
      }));
    }, [contracts, groupByCustomer]);

    const toggleCustomer = (id: string) => {
      setExpandedCustomers((prev) => ({
        ...prev,
        [id]: !prev[id],
      }));
    };

    const handleExpandAll = () => {
      // Expand all customers
      const allCustomers = contracts.reduce(
        (acc, contract) => {
          const customerField = contract.customer;
          const customerId = typeof customerField === 'string' ? customerField : customerField?.id;
          if (customerId) {
            acc[customerId] = true;
          }
          return acc;
        },
        {} as Record<string, boolean>
      );

      setExpandedCustomers(allCustomers);
    };

    const handleCollapseAll = () => {
      setExpandedCustomers({});
    };

    const handleZoomIn = () => {
      timelineRef.current?.handleZoomIn();
    };

    const handleZoomOut = () => {
      timelineRef.current?.handleZoomOut();
    };

    useImperativeHandle(ref, () => ({
      handleExpandAll,
      handleCollapseAll,
      handleZoomIn,
      handleZoomOut,
    }));

    const handleNavigate = (contract: Contract) => {
      const queryString = searchParams.toString();
      const searchQuery = queryString ? `?${queryString}` : '';

      navigate(`${basePath}/${contract.id}${searchQuery}`);
    };

    // Create customer timeline items
    const customerTimelineItems = customerSpans.map((span) =>
      CustomerTimelineItem({
        customerId: span.customerId,
        startTime: span.startTime,
        endTime: span.endTime,
      })
    );

    const { timelineGroups, timelineItems } = groupByCustomer
      ? // Group by customer
        {
          timelineGroups: Object.values(
            contracts.reduce(
              (acc, contract) => {
                const customerField = contract.customer;
                const customerId =
                  typeof customerField === 'string' ? customerField : customerField?.id;
                if (!customerId) return acc;

                if (!acc[customerId]) {
                  acc[customerId] = {
                    id: customerId,
                    title: null,
                    contracts: [],
                  };
                }
                acc[customerId].contracts.push(contract);
                return acc;
              },
              {} as Record<string, { id: string; title: null; contracts: Contract[] }>
            )
          ).flatMap((customerGroup) => {
            return [
              // Customer group
              {
                id: `customer-${customerGroup.id}` as Id,
                title: (
                  <CustomerGroup
                    id={customerGroup.id}
                    isExpanded={expandedCustomers[customerGroup.id]}
                    onToggle={() => toggleCustomer(customerGroup.id)}
                  />
                ),
                root: true,
                height: 40,
              },
              // Contracts under the customer (flat) when expanded
              ...(expandedCustomers[customerGroup.id]
                ? customerGroup.contracts.map((o) => ({
                    id: o.id as Id,
                    title: <div className="pl-4 text-sm truncate">{o.title}</div>,
                    root: false,
                  }))
                : []),
            ];
          }),
          timelineItems: [
            // Customer-level timeline items that span from earliest to latest contract dates
            ...customerTimelineItems,
            // Regular contract items
            ...contracts
              .filter((contract) => {
                const customerField = contract.customer;
                const customerId =
                  typeof customerField === 'string' ? customerField : customerField?.id;
                return customerId ? expandedCustomers[customerId] : false;
              })
              .map((contract) => ({
                id: contract.id,
                group: contract.id,
                title: (
                  <div className="flex gap-1">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <div className="relative size-5">
                          <Button
                            variant="ghost"
                            className="absolute left-0 top-1 size-5 p-0 text-muted-foreground hover:text-foreground"
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <span className="sr-only">{t('navigation.openMenu')}</span>
                            <MoreHorizontal className="size-4" />
                          </Button>
                        </div>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        align="end"
                        onMouseDown={(e) => e.stopPropagation()}
                        onMouseUp={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            handleNavigate(contract);
                          }}
                        >
                          {t('common.edit')}
                        </DropdownMenuItem>
                        {onCopy && (
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              onCopy(contract);
                            }}
                          >
                            {t('contracts.copyContract')}
                          </DropdownMenuItem>
                        )}
                        {onDelete && (
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              onDelete(contract);
                            }}
                          >
                            {t('common.delete')}
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                    <span className="text-foreground">{contract.title}</span>
                  </div>
                ),
                start_time: DateTime.fromISO(contract.startDate!).toMillis(),
                end_time: DateTime.fromISO(contract.endDate!).toMillis(),
                className: cn('bg-muted! border-none!'),
              })),
          ],
        }
      : // Show each contract as its own group
        {
          timelineGroups: contracts.map((contract) => ({
            id: contract.id,
            title: <div className="ml-2 text-sm truncate">{contract.title}</div>,
          })),
          timelineItems: contracts.map((contract) => ({
            id: contract.id,
            group: contract.id,
            title: (
              <div className="flex gap-1">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <div className="relative size-5">
                      <Button
                        variant="ghost"
                        className="absolute left-0 top-1 size-5 p-0 text-muted-foreground hover:text-foreground"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <span className="sr-only">{t('navigation.openMenu')}</span>
                        <MoreHorizontal className="size-4" />
                      </Button>
                    </div>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    onMouseDown={(e) => e.stopPropagation()}
                    onMouseUp={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        handleNavigate(contract);
                      }}
                    >
                      {t('common.edit')}
                    </DropdownMenuItem>
                    {onCopy && (
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          onCopy(contract);
                        }}
                      >
                        {t('contracts.copyContract')}
                      </DropdownMenuItem>
                    )}
                    {onDelete && (
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(contract);
                        }}
                      >
                        {t('common.delete')}
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
                <span>{contract.title}</span>
              </div>
            ),
            start_time: DateTime.fromISO(contract.startDate!).toMillis(),
            end_time: DateTime.fromISO(contract.endDate!).toMillis(),
            className: cn('bg-muted! border-none!'),
          })),
        };

    return (
      <TimelineView
        ref={timelineRef}
        groups={timelineGroups}
        items={timelineItems}
        startTime={DateTime.now().minus({ months: 1 }).toMillis()}
        endTime={DateTime.now().plus({ months: 12 }).toMillis()}
        horizontalLineClassNamesForGroup={(group) => (group.root ? ['row-root'] : [])}
        onItemClick={(itemId) => {
          const contract = contracts.find((o) => o.id === itemId);
          if (contract) {
            handleNavigate(contract);
          }
        }}
      />
    );
  }
);

ContractsTimeline.displayName = 'ContractsTimeline';
